import { FC } from 'react';
import { useSelector } from 'react-redux';

import Button, { Color, Size } from 'lib/Button';

import Tooltip, { TooltipDirection, TooltipSize } from 'lib/Tooltip';

import { FrontEndType } from 'components/common/types/Deal.types';
import { Store } from 'components/common/types/Store.types';

import {
  getIsAnyLineFetchingAvailability,
  getIsLineBeingSaved,
  getLoading,
  getActionConditions,
} from 'store/dealManagement/selectors';
import { getIsDirectSalesCampaignType } from 'store/dealManagement/selectors/getCampaignType';
import { Loading } from 'store/dealManagement/types';
import usePendoTracking from 'customHooks/usePendoTracking';
import { getDealId } from 'store/dealManagement/selectors/getDealId';
import { getIsEditingDisabled } from 'store/dealManagement/selectors/getIsEditingDisabled';

type AddNewLineButtonProps = {
  label?: string;
  isDisabled?: boolean;
  onClick: () => void;
};

const AddNewLineButton: FC<AddNewLineButtonProps> = ({ label = 'New deal line', isDisabled, onClick }) => {
  const dealId = useSelector(getDealId);
  const frontEndType = useSelector((state: Store) => state.dealManagement.commonDeal.currentLine.frontEndType);
  const isEditingDisabled = useSelector(getIsEditingDisabled);
  const isFetchingAvailability = useSelector(getIsAnyLineFetchingAvailability);
  const isLineBeingSaved = useSelector(getIsLineBeingSaved);
  const isLoadingPoiData = useSelector(getLoading)(Loading.GET_POI_DATA);

  const isDirectSalesCampaignType = useSelector(getIsDirectSalesCampaignType);
  const { getPendoDataId } = usePendoTracking();
  const { createLine } = useSelector(getActionConditions);

  const noDirectSalesCampaign = isDirectSalesCampaignType && !dealId;

  const isAddNewLineButtonDisabled =
    frontEndType === FrontEndType.PLANNER
      ? isDisabled ||
        isEditingDisabled ||
        isFetchingAvailability ||
        isLineBeingSaved ||
        noDirectSalesCampaign ||
        isLoadingPoiData
      : createLine.disabled;

  return (
    <Tooltip
      tooltip={label}
      direction={TooltipDirection.BOTTOM}
      contentSize={TooltipSize.MAX}
      wrapperClassName="flex items-center"
    >
      <Button
        classNames="flex flex-row-reverse items-center leading-tight border border-transparent"
        onClick={onClick}
        color={Color.TRANSPARENT}
        size={Size.SMALL}
        isDisabled={isAddNewLineButtonDisabled}
        label={`+ ${label}`}
        dataPendoId={getPendoDataId('New deal line')}
      />
    </Tooltip>
  );
};

export default AddNewLineButton;
